import { useContext, useEffect, useRef, useState } from "react";
import { GlobeAltIcon, CheckIcon } from "@heroicons/react/24/outline";
import { I18nContext, useTranslation } from "../../context/I18nContext";

// A small header-style dropdown (same interaction shape as NotificationBell:
// icon button -> absolutely-positioned panel, closed on outside click) that
// lets the current user switch languages from anywhere in the app. Used in
// MainLayout's header (authenticated pages) and standalone on Login (pre-
// authentication — I18nProvider still resolves a language there from
// localStorage/the backend's default, see I18nContext's own doc comment).
//
// Only languages the admin left IsActive show up here — a language an admin
// disables (but doesn't delete) simply stops being an option to switch
// *into*, without breaking anyone already using it.
export default function LanguagePicker({ variant = "header" }) {
  const { languages, languageCode, switchLanguage } = useContext(I18nContext);
  // "common" (not a dedicated namespace) because this component is also
  // rendered standalone on the Login page, before any "header" bundle
  // would ever load — one shared key here covers both places instead of
  // duplicating the same word into a page-specific namespace.
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const activeLanguages = (languages || []).filter((l) => l.is_active);
  const current = activeLanguages.find((l) => l.code === languageCode);

  if (activeLanguages.length === 0) return null;

  const isLight = variant === "light";

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={
          isLight
            ? "flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-gray-500 hover:bg-gray-50 transition-colors"
            : "notif-btn flex items-center gap-1"
        }
        title={t("language_picker_title", "Language")}
      >
        <GlobeAltIcon className="w-[20px] h-[20px]" />
        <span className="uppercase text-[11px] font-bold">{current?.code || languageCode || ""}</span>
      </button>

      {open && (
        /* end-0 بدل right-0 عشان القائمة المنسدلة تفتح من الجهة الصح
           (تتقلب تلقائيًا مع اتجاه اللغة) */
        <div className="absolute end-0 mt-2 w-48 max-w-[90vw] bg-white rounded-2xl border border-gray-100 shadow-xl z-[10000] overflow-hidden">
          <div className="max-h-72 overflow-y-auto py-1">
            {activeLanguages.map((l) => (
              <button
                key={l.code}
                onClick={() => {
                  switchLanguage(l.code);
                  setOpen(false);
                }}
                className="w-full flex items-center justify-between gap-2 text-start px-4 py-2.5 text-xs font-bold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <span>{l.name}</span>
                {l.code === languageCode && <CheckIcon className="w-4 h-4 text-[var(--primary-color)]" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
